import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { BrowserRouter, Routes } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { useSystemSettings } from "@/features/admin/hooks/useSystemSettings";
import { useAuth } from "@/contexts/AuthContext";
import { SecurityGuard } from "@/components/SecurityGuard";
import { usePerformanceCache } from "@/features/restaurant/hooks/usePerformanceCache";
import { QueryProvider } from "@/providers/QueryProvider";
import { SyncIndicator } from "@/components/SyncIndicator";
import React, { lazy, Suspense } from 'react';

// Route modules
import { publicRoutes } from "@/routes/publicRoutes";
import { customerRoutes } from "@/routes/customerRoutes";
import { restaurantRoutes } from "@/routes/restaurantRoutes";
import { adminRoutes } from "@/routes/adminRoutes";

// Lazy load maintenance page
const Maintenance = lazy(() => import("./pages/Maintenance"));

// Lazy load loyalty notification
const LoyaltyNotification = lazy(() => import("@/features/loyalty/components/LoyaltyNotification").then(m => ({ default: m.LoyaltyNotification })));

// Loading fallbacks
const MinimalSpinner = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
);

const PageSkeleton = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2" />
      <p className="text-sm text-muted-foreground">Carregando...</p>
    </div>
  </div>
);

const MaintenanceWrapper = () => {
  const { settings, loading: settingsLoading, error: settingsError } = useSystemSettings();
  const { user, userRole, loading: authLoading } = useAuth();

  if (settingsLoading || authLoading) {
    return <MinimalSpinner />;
  }

  if (settingsError) {
    // Settings error - allow access gracefully
  }

  if (settings.maintenance_mode === true) {
    const isLoggedInAdmin = user && userRole === 'admin';
    if (!isLoggedInAdmin) {
      return (
        <Suspense fallback={<MinimalSpinner />}>
          <Maintenance />
        </Suspense>
      );
    }
  }

  return <AppContent />;
};

const AppContent = () => {
  const { prefetchData } = usePerformanceCache();
  
  React.useEffect(() => {
    const prefetchCriticalData = async () => {
      try {
        prefetchData(
          'restaurants-approved',
          () => import('@/features/restaurant/services/restaurantService').then(
            service => service.restaurantService.getApprovedRestaurants()
          ),
          'high'
        );
      } catch (error) {
        // Prefetch failed silently
      }
    };
    
    setTimeout(prefetchCriticalData, 100);
  }, [prefetchData]);

  return (
    <>
      <SecurityGuard />
      <Suspense fallback={null}>
        <LoyaltyNotification />
      </Suspense>
      <Suspense fallback={<PageSkeleton />}>
        <Routes>
          {publicRoutes}
          {customerRoutes}
          {restaurantRoutes}
          {adminRoutes}
        </Routes>
      </Suspense>
    </>
  );
};

const App = () => (
  <ErrorBoundary>
    <QueryProvider>
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <SyncIndicator />
              <MaintenanceWrapper />
            </TooltipProvider>
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryProvider>
  </ErrorBoundary>
);

export default App;
